import { useConfig } from '@revenuewell/logic-config';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import React, { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import ProductService from '../../services/product-service/product-service';

interface ProductsContextType {
  hasProduct: (product: string, locationId?: number) => boolean;
  getProductsForLocation: (locationId: number) => Promise<string[]>;
  isLoading: (locationId: number) => boolean;
}

interface ProductsProviderProps {
  children: ReactNode;
}

export const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

const ProductsByLocationProvider: React.FC<ProductsProviderProps> = ({ children }) => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const [productsByLocation, setProductsByLocation] = useState<{ [id: number]: string[] }>({});
  const [loadingLocations, setLoadingLocations] = useState<{ [id: number]: boolean }>({});

  const productService = useMemo(() =>
    new ProductService(config, oidcService),
    [config, oidcService]
  );

  const getProductsForLocation = useCallback(async (locationId: number): Promise<string[]> => {
    if (productsByLocation[locationId]) {
      return productsByLocation[locationId];
    }

    setLoadingLocations(prev => ({
      ...prev,
      [locationId]: true
    }));

    try {
      const newProducts = await productService.getAvailableProducts(locationId);
      const lowercased = newProducts.map(p => p.toLowerCase());

      setProductsByLocation(prev => ({
        ...prev,
        [locationId]: lowercased
      }));

      return lowercased;
    } finally {
      setLoadingLocations(prev => ({
        ...prev,
        [locationId]: false
      }));
    }
  }, [productService, productsByLocation]);

  const hasProduct = useCallback((product: string, locationId?: number) => {
    if (!locationId) return false;

    if (productsByLocation[locationId]) {
      return productsByLocation[locationId].includes(product.toLowerCase());
    }

    return false;
  }, [productsByLocation]);

  const isLoading = useCallback((locationId: number) => loadingLocations[locationId] ?? true, [productsByLocation, loadingLocations]);

  const value = useMemo(() => ({
    hasProduct,
    getProductsForLocation,
    isLoading
  }), [hasProduct, getProductsForLocation, isLoading]);

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export default ProductsByLocationProvider;
